import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  useUpdateEPAMutation,
  useDeleteEPAMutation,
  getEPADetailsQuery,
} from '@/app/hooks/queries/useEarlyPhaseAssessment';
import {
  Field,
  FieldControl,
  FieldDescription,
  FieldError,
  FieldLabel,
} from '@/app/ui/components/form/field';
import { Button } from '@/app/ui/components/button';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogPopup,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/app/ui/components/alert-dialog';
import { useTranslations } from 'use-intl';
import { useEffect, useState } from 'react';
import { Form } from '@/app/ui/components/form';
import {
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectTriggerIcon,
  SelectValue,
} from '@/app/ui/components/select';
import { LangEnum } from '@/app/utils/constants';
import { getLangIcon } from '@/app/utils/language';
import { Card } from '@/app/ui/components/card';
import { H3 } from '@/app/ui/components/heading';
import { useParams } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/early-phase-assessments/$earlyPhaseAssessmentId/_layout/(home)/settings/_layout/',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages.tenderPackageSettings');
  const tGlobal = useTranslations();
  const { earlyPhaseAssessmentId, projectId } = useParams({
    from: '/_authed/project/$projectId/_layout/(evaluation)/early-phase-assessments/$earlyPhaseAssessmentId/_layout/(home)/settings/_layout',
  });
  const navigate = useNavigate();
  const { data: evaluation } = useSuspenseQuery(getEPADetailsQuery(earlyPhaseAssessmentId));
  const { mutateAsync: updateEPA, isPending: isUpdating } = useUpdateEPAMutation();
  const { mutateAsync: deleteEPA, isPending: isDeleting } = useDeleteEPAMutation({});

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [name, setName] = useState('');
  const [language, setLanguage] = useState<string>(LangEnum.en);

  useEffect(() => {
    if (name === '') {
      setName(evaluation.name ?? '');
      setLanguage(evaluation.language ?? LangEnum.en);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evaluation]);

  const handleSubmit = async () => {
    await updateEPA({
      name,
      earlyPhaseAssessmentId,
      language,
    });
  };

  const handleDelete = async () => {
    await deleteEPA(earlyPhaseAssessmentId);
    navigate({ to: '/project/$projectId', params: { projectId } });
    setDeleteDialogOpen(false);
  };

  return (
    <>
      <Form onFormSubmit={handleSubmit} className="max-w-xl w-full flex flex-col gap-4">
        <Card className="space-y-4">
          <H3 className="-mt-1">{t('info')}</H3>
          <Field>
            <FieldLabel>{t('formLabels.name')}</FieldLabel>
            <FieldControl
              required
              value={name}
              onValueChange={setName}
              placeholder={t('formLabels.name')}
              maxLength={100}
            />
            <FieldDescription>{t('formLabels.nameDescription')}</FieldDescription>
            <FieldError />
          </Field>
        </Card>
        <Card className="space-y-4">
          <H3 className="-mt-1">{t('analysisSettings')}</H3>
          <Field>
            <FieldLabel>{t('formLabels.language')}</FieldLabel>
            <Select required value={language} onValueChange={(lang) => setLanguage(lang as never)}>
              <SelectTrigger size="lg" className={'w-full'}>
                <SelectValue>
                  {(v) => (
                    <>
                      {getLangIcon(v as LangEnum)}
                      {tGlobal(
                        // @ts-expect-error ...
                        `general.languages.${v}`,
                      )}
                    </>
                  )}
                </SelectValue>
                <SelectTriggerIcon />
              </SelectTrigger>
              <SelectPopup>
                {Object.values(LangEnum).map((lang) => (
                  <SelectItem key={lang} value={lang}>
                    {getLangIcon(lang)}
                    {tGlobal(`general.languages.${lang}`)}
                  </SelectItem>
                ))}
              </SelectPopup>
            </Select>
            <FieldError />
          </Field>
        </Card>
        <Button type="submit" pending={isUpdating} disabled={isUpdating} className="ml-auto">
          {t('buttons.save')}
        </Button>
        <Card>
          <H3 className="-mt-1">{t('danger')}</H3>
          <p className="text-sm text-muted mt-2 mb-4">{t('dangerText')}</p>
          <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <AlertDialogTrigger
              render={<Button variant="destructive">{t('deleteConfirmModal.title')}</Button>}
            />
            <AlertDialogPopup>
              <AlertDialogTitle>{t('deleteConfirmModal.title')}</AlertDialogTitle>
              <AlertDialogDescription>
                {tGlobal.rich('pages.earlyPhaseAssessmentSettings.deleteConfirmModal.subtitle', {
                  epaName: evaluation.name,
                  b: (chunks) => <strong>{chunks}</strong>,
                })}
              </AlertDialogDescription>
              <AlertDialogFooter>
                <AlertDialogCancel />
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  pending={isDeleting}
                  disabled={isDeleting}
                >
                  {t('buttons.delete')}
                </Button>
              </AlertDialogFooter>
            </AlertDialogPopup>
          </AlertDialog>
        </Card>
      </Form>
    </>
  );
}
